"use client";

import { motion } from "motion/react";
import { ReactNode } from "react";
import { cn } from "@/lib/utils";

type Box = { label: string; body: string; icon?: ReactNode; accent?: "red" | "coral" | "purple" | "cyan" | "denim" | "emerald" | "lime" | "amber-gold" | "crimson" };

const ACCENT_TEXT: Record<NonNullable<Box["accent"]>, string> = {
  red: "text-red-400", coral: "text-coral-400", purple: "text-purple-400", cyan: "text-cyan-400", denim: "text-denim-400", emerald: "text-emerald-400", lime: "text-lime-400", "amber-gold": "text-amber-gold-400", crimson: "text-crimson-400",
};
const ACCENT_BG: Record<NonNullable<Box["accent"]>, string> = {
  red: "from-red-500/20 to-red-500/0", coral: "from-coral-500/20 to-coral-500/0", purple: "from-purple-500/20 to-purple-500/0", cyan: "from-cyan-500/20 to-cyan-500/0", denim: "from-denim-500/20 to-denim-500/0", emerald: "from-emerald-500/20 to-emerald-500/0", lime: "from-lime-500/20 to-lime-500/0", "amber-gold": "from-amber-gold-500/20 to-amber-gold-500/0", crimson: "from-crimson-500/20 to-crimson-500/0",
};
const ACCENT_BORDER: Record<NonNullable<Box["accent"]>, string> = {
  red: "border-red-500/40", coral: "border-coral-500/40", purple: "border-purple-500/40", cyan: "border-cyan-500/40", denim: "border-denim-500/40", emerald: "border-emerald-500/40", lime: "border-lime-500/40", "amber-gold": "border-amber-gold-500/40", crimson: "border-crimson-500/40",
};

export function ArchitectureDiagramBlock({ center, satellites, connections, title, eyebrow, description }: { center: Box; satellites: Box[]; connections: { from: number; to: number; label?: string }[]; title?: string; eyebrow?: string; description?: string }) {
  const total = satellites.length;
  const radius = 200;
  const center2 = 240;

  return (
    <div className="space-y-10">
      {(eyebrow || title || description) && (
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mx-auto max-w-3xl text-center">
          {eyebrow && <div className="mb-3 text-xs font-bold uppercase tracking-[0.3em] text-amber-gold-500">{eyebrow}</div>}
          {title && <h2 className="font-rajdhani text-3xl font-bold text-foreground lg:text-4xl">{title}</h2>}
          {description && <p className="mt-3 text-foreground/80/80">{description}</p>}
        </motion.div>
      )}
      <div className="relative mx-auto" style={{ width: center2 * 2, height: center2 * 2 }}>
        <svg className="absolute inset-0" viewBox={`0 0 ${center2 * 2} ${center2 * 2}`}>
          {connections.map((c, i) => {
            const f = c.from === -1 ? { x: center2, y: center2 } : (() => {
              const angle = (c.from / total) * 2 * Math.PI - Math.PI / 2;
              return { x: center2 + Math.cos(angle) * radius, y: center2 + Math.sin(angle) * radius };
            })();
            const angle2 = (c.to / total) * 2 * Math.PI - Math.PI / 2;
            const t = { x: center2 + Math.cos(angle2) * radius, y: center2 + Math.sin(angle2) * radius };
            return <line key={i} x1={f.x} y1={f.y} x2={t.x} y2={t.y} stroke="rgb(82, 82, 91)" strokeWidth="1" strokeDasharray="3 3" />;
          })}
        </svg>
        <motion.div initial={{ opacity: 0, scale: 0.5 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ duration: 0.5 }} className="absolute left-1/2 top-1/2 max-w-[180px] -translate-x-1/2 -translate-y-1/2">
          <div className="glass-card relative overflow-hidden rounded-2xl border-2 border-amber-gold-500/60 p-5 text-center">
            <div className="absolute inset-0 bg-gradient-to-br from-amber-gold-500/20 to-amber-gold-500/0" />
            <div className="relative">
              {center.icon && <div className="mb-2 flex justify-center text-amber-gold-500">{center.icon}</div>}
              <div className="font-rajdhani text-base font-bold text-foreground">{center.label}</div>
              <p className="mt-2 text-xs text-foreground/80/80">{center.body}</p>
            </div>
          </div>
        </motion.div>
        {satellites.map((s, i) => {
          const angle = (i / total) * 2 * Math.PI - Math.PI / 2;
          const x = center2 + Math.cos(angle) * radius;
          const y = center2 + Math.sin(angle) * radius;
          const accent = s.accent ?? "purple";
          return (
            <motion.div key={i} initial={{ opacity: 0, scale: 0.5 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.08 }} className="absolute max-w-[180px] -translate-x-1/2 -translate-y-1/2" style={{ left: x, top: y }}>
              <div className={cn("glass-card relative overflow-hidden rounded-xl border p-3 text-center", ACCENT_BORDER[accent])}>
                <div className={cn("absolute inset-0 bg-gradient-to-br", ACCENT_BG[accent])} />
                <div className="relative">
                  {s.icon && <div className={cn("mb-1 flex justify-center", ACCENT_TEXT[accent])}>{s.icon}</div>}
                  <div className="font-rajdhani text-xs font-bold text-foreground">{s.label}</div>
                  <p className="mt-1 text-[10px] leading-relaxed text-foreground/80/80">{s.body}</p>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
